class Carousel extends HTMLElement {
  constructor() {
    super();
    this.id = this.dataset.id;
    this.swiperEl = this.querySelector(".swiper");
    this.showNavigation = this.dataset.showNavigation === "true";
    this.spaceBetween = parseInt(this.dataset.spaceBetween, 10);
    this.disableBelowBreakpoint = this.dataset.disableBelowBreakpoint;
    this.disableAboveBreakpoint = this.dataset.disableAboveBreakpoint;
    this.slides = this.querySelectorAll(".swiper-slide");

    window.loadSwiper.then(({ Swiper, Navigation, EffectFade, Pagination }) => {
      this.Swiper = Swiper;
      this.Navigation = Navigation;
      this.EffectFade = EffectFade;
      this.Pagination = Pagination;

      if (this.disableBelowBreakpoint || this.disableAboveBreakpoint) {
        if (this.disableAboveBreakpoint) {
          !window.matchMedia(`(min-width: ${window.themeBreakpoints[this.disableAboveBreakpoint]}px)`).matches &&
            this.initSwiper();
          window.addEventListener("resize", () => this.handleDisableAboveResize());
        } else {
          !window.matchMedia(`(max-width: ${window.themeBreakpoints[this.disableBelowBreakpoint]}px)`).matches &&
            this.initSwiper();
          window.addEventListener("resize", () => this.handleDisableBelowResize());
        }
      } else {
        this.initSwiper();
      }
    });
  }

  initSwiper() {
    const swiperParams = {
      modules: [],
      slidesPerView: "auto",
      spaceBetween: this.spaceBetween,
      breakpoints: this.getBreakpointsConfig(),
    };

    swiperParams.on = {
      init: (swiper) => {
        if (this.dataset.fireEvents === "true") {
          this.dispatchCustomEvent("swiper:init", {
            id: this.id,
            swiper: this.swiper,
          });
        }

        if (this.dataset.navigationAlignmentTarget) {
          this.updateNavPosition(swiper);
        }
      },
      slideChange: (swiper) => {
        if (this.dataset.fireEvents === "true") {
          this.dispatchCustomEvent("swiper:change", {
            id: this.id,
            activeIndex: swiper.activeIndex,
            swiper: swiper,
          });
        }

        if (this.dataset.navigationAlignmentTarget) {
          this.updateNavPosition(swiper);
        }
      },
      resize: (swiper) => {
        if (this.dataset.navigationAlignmentTarget) {
          this.updateNavPosition(swiper);
        }
      },
    };

    if (this.dataset.showNavigation === "true") {
      swiperParams.modules.push(this.Navigation);
      swiperParams.navigation = {
        nextEl: this.querySelector("[data-navigation-next]"),
        prevEl: this.querySelector("[data-navigation-previous]"),
      };
    }

    if (this.dataset.showPagination === "true") {
      swiperParams.modules.push(this.Pagination);
      swiperParams.pagination = {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      };
    }

    if (this.dataset.shouldFade === "true") {
      swiperParams.modules.push(this.EffectFade);
      swiperParams.effect = "fade";
      swiperParams.fadeEffect = {
        crossFade: true,
      };
    }

    if (this.dataset.shouldLoop === "true") {
      swiperParams.loop = true;
    }

    if (this.dataset.autoHeight === "true") {
      swiperParams.autoHeight = true;
    }

    if (this.dataset.centerInsufficientSlides === "true") {
      swiperParams.centerInsufficientSlides = true;
    }

    if (this.dataset.autoPlay !== "0") {
      swiperParams.autoplay = {
        delay: parseInt(this.dataset.autoPlay, 10),
      };
    }

    this.swiper = new this.Swiper(this.swiperEl, swiperParams);
  }

  handleDisableAboveResize() {
    if (window.matchMedia(`(min-width: ${window.themeBreakpoints[this.disableAboveBreakpoint]}px)`).matches) {
      this.swiper?.destroy(true, true);
      this.swiper = null;
    } else {
      !this.swiper && this.initSwiper();
    }
  }

  handleDisableBelowResize() {
    if (window.matchMedia(`(max-width: ${window.themeBreakpoints[this.disableBelowBreakpoint]}px)`).matches) {
      this.swiper?.destroy(true, true);
      this.swiper = null;
    } else {
      !this.swiper && this.initSwiper();
    }
  }

  getBreakpointsConfig() {
    const breakpoints = {};

    const isCenteredDefault = this.dataset.centerSlides === "true";
    const isCenteredBoundsDefault = this.dataset.centerSlidesBounds === "true";
    const baseConfig = {
      spaceBetween: this.spaceBetween,
      centeredSlides: isCenteredDefault,
      centeredSlidesBounds: isCenteredBoundsDefault,
    };

    breakpoints[0] = { ...baseConfig };

    const sortedBreakpoints = Object.entries(window.themeBreakpoints)
      .map(([key, val]) => [key, parseInt(val, 10)])
      .sort(([, a], [, b]) => a - b);

    let currentConfig = { ...baseConfig };

    sortedBreakpoints.forEach(([key, pixelValue]) => {
      const spacingKey = this.convertToDatasetKey(key);

      if (this.dataset[spacingKey] !== undefined) {
        currentConfig.spaceBetween = parseInt(this.dataset[spacingKey], 10);
      }

      if (key === this.dataset.disableCenterSlidesAbove) {
        currentConfig.centeredSlides = false;
      }

      if (key === this.dataset.disableCenterSlidesBoundsAbove) {
        currentConfig.centeredSlidesBounds = false;
      }

      breakpoints[pixelValue] = { ...currentConfig };
    });

    return breakpoints;
  }

  convertToDatasetKey = (key) => {
    const parts = key.split("-");
    const capitalizedParts = parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1));

    return `bpSpacing${capitalizedParts.join("")}`;
  };

  goToSlide(index) {
    this.swiper?.slideTo(index);
  }

  update() {
    this.swiper?.update();
  }

  dispatchCustomEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  updateNavPosition(swiperInstance) {
    const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
    if (!activeSlide) return;

    const imgContainer = activeSlide.querySelector(`.${this.dataset.navigationAlignmentTarget}`);
    if (!imgContainer) return;

    const imgHeight = imgContainer.offsetHeight;

    this.style.setProperty("--current-alignment-height", `${imgHeight}px`);
  }
}

if (!customElements.get("component-carousel")) {
  customElements.define("component-carousel", Carousel);
}
